"use client";

import { MapPinIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState, useEffect, useRef, FC } from "react";
import ClearDataButton from "./ClearDataButton";
import LatLng from "models/Latlng";
import toast from "react-hot-toast";
import { useData } from "data/data-provider";
import { getNearbyLocations, searchLocations } from "api/locations/locations";
import LocationModal from "./LocationModal";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  sameDropOffLocation?: boolean;
  fieldClassName?: string;
  variant?: "default" | "box";
  hideIconAtLg?: boolean;
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "Location",
  fieldClassName = "[ nc-hero-field-padding ]",
  desc = "Where are you going?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  sameDropOffLocation,
  variant = "default",
  hideIconAtLg = false,
}) => {
  const {
    ourLocation,
    setPickupLocation,
    searchedPickupLoction,
    setSearchedPickupLocation,
    pickupLocation,
    ourCompany,
    sameDropoffLocation,
  } = useData();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverBtnRef = useRef<HTMLButtonElement>(null);

  const [searchedLocations, setSearchedLocations] = useState<any>();
  const [showPopover, setShowPopover] = useState(autoFocus);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const callSearchLocationsAPI = async (input: string) => {
    try {
      const data = (await searchLocations(input)) as any;
      setSearchedLocations(data.predictions);
    } catch (error: any) {
      toast.error(error.message); // Or handle errors in a more sophisticated way
    }
  };

  useEffect(() => {
    // Only call API if search string has at least 3 characters to prevent errors on page load
    if (searchedPickupLoction && searchedPickupLoction.trim().length >= 3) {
      callSearchLocationsAPI(searchedPickupLoction);
    }
  }, [searchedPickupLoction]);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  useEffect(() => {
    if (pickupLocation === null) {
      setSearchedPickupLocation(""); // Clear state
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field display
      }
    }
  }, [pickupLocation, setSearchedPickupLocation]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    setShowPopover(false);
  };

  const handleSelectLocation = (item: any) => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    if (item.radius) {
      setSelectedLocation(item);
      setShowLocationModal(true);
      setShowPopover(false);
       popoverBtnRef.current?.click();
    } else {
      setPickupLocation(item);
      setShowPopover(false);
       popoverBtnRef.current?.click();
    }
  };

  const handleClear = () => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    setShowPopover(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const renderRecentSearches = () => {
    return (
      <div className="mt-2">
        {ourLocation
          .filter((item) => item.active !== false)
          .sort((a, b) => a.price - b.price)
          .map((item) => (
            <span
              onClick={() => handleSelectLocation(item)}
              key={item.id}
              className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-primary-200 hover:text-white  cursor-pointer justify-between"
            >
              <div className="flex ">
                <span className="block text-neutral-400 ">
                  <MapPinIcon className="h-4 sm:h-6 w-4 sm:w-6" />
                </span>
                <span className="text-left block font-medium  mx-2">
                  {item.name}
                </span>
              </div>
              <span className="block font-medium ">
                {item.price === 0 ? "Free" : "$" + item.price + "/trip"}
              </span>
            </span>
          ))}
      </div>
    );
  };

  return (
    <Popover className={`relative flex`} ref={containerRef}>
      {({ open }) => (
        <>
          <PopoverButton
            ref={popoverBtnRef}
            className={`flex w-full z-50 bg-gradient-to-b from-[#333333] to-[#222222] relative flex-shrink-0  ${fieldClassName} items-center space-x-3 focus:outline-none ${
              variant === "box"
                ? "bg-[#1C1C1C] border border-[#FFFFFF1A] rounded-md px-3 py-2"
                : "p-3"
            }`}
          >
            <div className="flex items-center w-full">
              <div
                className={`text-neutral-300 dark:text-neutral-400 ${
                  hideIconAtLg ? "block lg:hidden xl:block" : ""
                }`}
              >
                <svg
                  width="24"
                  height="17"
                  viewBox="0 0 24 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.375 15.6875C0.8125 9.125 0 8.4375 0 6C0 2.6875 2.65625 0 6 0C9.3125 0 12 2.6875 12 6C12 8.4375 11.1562 9.125 6.59375 15.6875C6.3125 16.125 5.65625 16.125 5.375 15.6875ZM6 8.5C7.375 8.5 8.5 7.40625 8.5 6C8.5 4.625 7.375 3.5 6 3.5C4.59375 3.5 3.5 4.625 3.5 6C3.5 7.40625 4.59375 8.5 6 8.5Z"
                    fill="#A9A9A9"
                  />
                </svg>
              </div>
              <div className="flex-grow min-w-0">
                <span
                  className={`block ${
                    !sameDropoffLocation ? "lg:max-w-28" : "w-full text-left"
                  } bg-transparent  text-left cursor-pointer p-0 !text-sm font-normal placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
                >
                  {searchedPickupLoction?.length > 0
                    ? searchedPickupLoction
                    : pickupLocation?.address || placeHolder}
                </span>
                <span
                  className={`${
                    variant === "box" ? "hidden" : "block"
                  } mt-0.5 text-xs text-neutral-400 font-light`}
                >
                  <span className="line-clamp-1">{desc}</span>
                </span>
                {(searchedPickupLoction || pickupLocation) && open && (
                  <ClearDataButton onClick={handleClear} />
                )}
              </div>
              {variant === "box" && (
                <ChevronDownIcon className="w-4 h-4 text-neutral-400" />
              )}
            </div>
          </PopoverButton>

          {open && variant !== "box" && (
            <div
              className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-50 ${divHideVerticalLineClass}`}
            ></div>
          )}

          {open &&
            ((searchedPickupLoction && searchedLocations?.length > 0) ||
              ourLocation.filter((item) => item.active !== false).length >
                0) && (
              <Transition
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <PopoverPanel className="absolute left-0 z-[10000] w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-[#444444] border border-[#FFFFFF1A] top-full mt-3 py-3 sm:py-6 rounded-xl shadow-xl max-h-96 overflow-y-auto hide-scrollbar">
                  {renderRecentSearches()}
                </PopoverPanel>
              </Transition>
            )}

          <LocationModal
            isOpen={showLocationModal}
            selectedLocation={selectedLocation}
            onClose={() => setShowLocationModal(false)}
            ourCompany={ourCompany}
            setSearchedPickupLocation={setSearchedPickupLocation}
            setPickupLocation={setPickupLocation}
            ourLocation={ourLocation}
          />
        </>
      )}
    </Popover>
  );
};

export default LocationInput;
